import { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, VSwitch, AutoCompleteFazenda, VTextFieldData, VFieldMoney, AutoCompleteGpPatrimonio, VTextFieldNumber, BasicSelect } from '../../../shared/forms';
import { FerramentasDeDetalhe } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { PatrimoniosService } from '../../../shared/services/api/cadastros/PatrimoniosService';
import SwipeableTextMobileStepper from './SwipeableTextMobileStepper';
import { DialogImagens } from './DialogImagens';
import { Environment } from '../../../shared/environment';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { DialogFiles } from './DialogFiles';
import MenuBookIcon from '@mui/icons-material/MenuBook';

type NullableDate = Date | null | undefined;

interface IFormData {
  descricao: string;
  numero: string;
  idfazenda: string;
  idgrupopatrimonio: string;
  dataaquisicao: Date;
  fabricante: string;
  valoraquisicao: number;
  marca: string;
  modelo: string;
  cor: string;
  ano: number;
  numeroserie: string;
  datagarantia: NullableDate;
  numeroapolice: string;
  databaixa: NullableDate;
  motivobaixa: string;
  obs: string;
  seguradora: string;
  status: boolean;
  veiculos: boolean,
  litros: number;
  tipomedicao: string;
  horimetro: number;
  fotos: IImage[];
  arquivo: IArquivos[];
}

interface IImage {
  imgPath: string;
  arquivo: File | undefined;
}

interface IArquivos {
  Path: string;
  arquivo: File | undefined;
}

const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'fotos' | 'arquivo'>> = yup.object().shape({
  descricao: yup.string().required().min(3).max(60),
  numero: yup.string().required().min(1).max(20),
  idfazenda: yup.string().required(),
  idgrupopatrimonio: yup.string().required(),
  dataaquisicao: yup.date().required(),
  fabricante: yup.string().optional().default(''),
  valoraquisicao: yup.number().required().moreThan(0),
  marca: yup.string().optional().default(''),
  modelo: yup.string().optional().default(''),
  cor: yup.string().optional().default(''),
  ano: yup.number().required().moreThan(0),
  numeroserie: yup.string().optional().default(''),
  datagarantia: yup.date().nullable(),
  numeroapolice: yup.string().optional().default(''),
  databaixa: yup.date().nullable().optional().when('status', {
    is: false,
    then: () => yup.date().nullable().required('Campo obrigatório')
  }),
  motivobaixa: yup.string().optional().default(''),
  obs: yup.string().optional().default(''),
  seguradora: yup.string().optional().default(''),
  status: yup.boolean().default(true),
  veiculos: yup.boolean().optional().default(false),
  litros: yup.number().default(0).when('veiculos', {
    is: true,
    then: () => yup.number().required('Campo obrigatório').moreThan(0)
  }),
  tipomedicao: yup.string().optional().default('').when('veiculos', {
    is: true,
    then: () => yup.string().required('Campo obrigatório')
  }),
  horimetro: yup.number().default(0).when('veiculos', {
    is: true,
    then: () => yup.number().required('Campo obrigatório').moreThan(0)
  }),
});

export const DetalheDePatrimonio: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();

  const navigate = useNavigate();

  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [veiculoss, setVeiculoss] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [images, setImages] = useState<IImage[]>([]);
  const [arquivos, setArquivos] = useState<IArquivos[]>([]);
  const [open, setOpen] = useState(false);
  const [openfiles, setOpenFiles] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openConfirm, setOpenConfirm] = useState(false);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate('/patrimonios');
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    PatrimoniosService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  const imagesnovo = [{ imgPath: `${Environment.URL_IMGPATRIMONIO}semimagem.png`, arquivo: undefined }];
  const arquivonovo = [{ Path: `${Environment.URL_IMGPATRIMONIO}semimagem.pdf`, arquivo: undefined }];

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      PatrimoniosService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao(result.descricao);
            setVeiculoss(result.veiculos);
            formRef.current?.setData(result);
            if (result.fotos === null || result.fotos.length === 0) {
              setImages(imagesnovo);
            } else {
              const imageList: IImage[] = [];
              result.fotos.forEach((item) => {
                imageList.push({ imgPath: Environment.URL_IMGPATRIMONIO + item.imgPath, arquivo: undefined });
              });
              setImages(imageList);
            }
            if (result.arquivo === null || result.arquivo.length === 0) {
              setArquivos(arquivonovo);
            } else {
              const filesList: IArquivos[] = [];
              result.arquivo.forEach((item) => {
                filesList.push({ Path: item.Path, arquivo: undefined });
              });
              setArquivos(filesList);
            }
          }
        });
    } else {
      formRef.current?.setData({
        descricao: '',
        numero: '',
        idfazenda: '',
        idgrupopatrimonio: '',
        fabricante: '',
        valoraquisicao: 0,
        marca: '',
        modelo: '',
        cor: '',
        ano: 0,
        numeroserie: '',
        numeroapolice: '',
        obs: '',
        seguradora: '',
        status: true,
        veiculos: false,
        datagarantia: null,
        dataaquisicao: '',
        horimetro: 0,
      });
      setImages(imagesnovo);
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (dadosValidados.databaixa) {
          dadosValidados.status = false;
        }
        if (id === 'novo') {
          PatrimoniosService
            .create(files, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }
            });
        } else {
          PatrimoniosService
            .updateById(id, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleveiculos = (veiculos: boolean | undefined) => {
    if (veiculos != null) {
      formRef.current?.setFieldValue('veiculos', veiculos);
      setVeiculoss(veiculos);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseFiles = () => {
    setOpenFiles(false);
  };

  const handleImg = (dados: IImage[]) => {
    if (dados.length === 0) {
      setImages(imagesnovo);
    } else {
      const fileList: File[] = [];
      const imageList: IImage[] = [];
      dados.forEach((item) => {
        if (item.arquivo) {
          const imageUrl = URL.createObjectURL(item.arquivo);
          fileList.push(item.arquivo);
          imageList.push({ imgPath: imageUrl, arquivo: item.arquivo });
        } else {
          imageList.push({ imgPath: item.imgPath, arquivo: undefined });
        }
      });
      setFiles(fileList);
      setImages(imageList);
    }
  };

  const handleFiles = (dados: IArquivos[]) => {
    if (dados.length === 0) {
      setArquivos(arquivonovo);
    } else {
      const fileList: File[] = [];
      const imageList: IArquivos[] = [];
      dados.forEach((item) => {
        if (item.arquivo) {
          const imageUrl = URL.createObjectURL(item.arquivo);
          fileList.push(item.arquivo);
          imageList.push({ Path: imageUrl, arquivo: item.arquivo });
        } else {
          imageList.push({ Path: item.Path, arquivo: undefined });
        }
      });
      setFiles(fileList);
      setArquivos(imageList);
    }
  };

  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Patrimônio' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressao={false}

          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/patrimonios')}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/patrimonios/detalhe/novo')}
          mostrarBotaoNovoCarregando={isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>;
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                  Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
                  Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} >
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid container item direction="row" spacing={2}>
              <Grid container spacing={1} item xs={12} sm={12} md={4} lg={4} xl={4}>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <SwipeableTextMobileStepper
                    images={images}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={9} lg={8} xl={12}>
                  <PhotoCameraIcon onClick={() => { setOpen(true); }} color='secondary' sx={{ fontSize: 28 }} />
                </Grid>
              </Grid>
              <Grid container item xs={12} sm={12} md={8} lg={8} xl={8}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Grid container item direction="row" spacing={1.5}>
                    <Grid item xs={12} sm={12} md={3} lg={4} xl={2}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='numero'
                        disabled={isLoading}
                        label='Número'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={9} lg={8} xl={4}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='descricao'
                        disabled={isLoading}
                        label='Descrição'
                        onChange={e => setDescricao(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                      <AutoCompleteGpPatrimonio
                        isExternalLoading={isLoading}
                        veiculos={handleveiculos}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                      <AutoCompleteFazenda
                        isExternalLoading={isLoading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={2}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='cor'
                        disabled={isLoading}
                        label='Cor'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='modelo'
                        disabled={isLoading}
                        label='Modelo'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={8} xl={3}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='marca'
                        disabled={isLoading}
                        label='Marca'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='numeroserie'
                        disabled={isLoading}
                        label='Número de Série'
                      />
                    </Grid>
                    <Grid item xs={3} sm={12} md={6} lg={4} xl={2}>
                      <VTextFieldNumber
                        fullWidth
                        size='small'
                        name='ano'
                        disabled={isLoading}
                        label='Ano'
                      />
                    </Grid>
                    <Grid item xs={9} sm={12} md={6} lg={4} xl={4}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='fabricante'
                        disabled={isLoading}
                        label='Fabricante'
                      />
                    </Grid>
                    <Grid item xs={6} sm={12} md={6} lg={4} xl={2}>
                      <VTextFieldData
                        fullWidth
                        size='small'
                        name='datagarantia'
                        disabled={isLoading}
                        label='Data Garantia'
                      />
                    </Grid>
                    <Grid item xs={6} sm={12} md={6} lg={4} xl={2}>
                      <VTextFieldData
                        fullWidth
                        size='small'
                        name='dataaquisicao'
                        disabled={isLoading}
                        label='Data Aquisição'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={2}>
                      <VFieldMoney
                        fullWidth
                        size='small'
                        name='valoraquisicao'
                        disabled={isLoading}
                        label='Valor Aquisição'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='seguradora'
                        disabled={isLoading}
                        label='Seguradora'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='numeroapolice'
                        disabled={isLoading}
                        label='Número Apólice'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={2}>
                      <BasicSelect
                        name='tipomedicao'
                        label='Tipo'
                        data={[
                          { nameBD: 'km', descricao: 'KM' },
                          { nameBD: 'horimetro', descricao: 'Horimetro' },
                        ]}
                        disabled={veiculoss ? isLoading : true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={2}>
                      <VFieldMoney
                        fullWidth
                        size='small'
                        name='litros'
                        disabled={veiculoss ? isLoading : true}
                        label='Qtd Litos Tanque'
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={2}>
                      <VFieldMoney
                        fullWidth
                        size='small'
                        name='horimetro'
                        disabled={veiculoss ? isLoading : true}
                        label='Horimetro'
                      />
                    </Grid>
                    <Grid item xs={6} sm={12} md={6} lg={12} xl={12}>
                    </Grid>
                    {(id != 'novo') && (
                      <><Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                        <VTextFieldData
                          fullWidth
                          name='databaixa'
                          disabled={isLoading}
                          label='Data da Baixa' />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
                        <VTextField
                          fullWidth
                          size='small'
                          name='motivobaixa'
                          disabled={isLoading}
                          label='Motivo da Baixa'
                        />
                      </Grid>
                      <Grid item xs={6} sm={12} md={6} lg={2} xl={1}>
                        <VSwitch
                          name='status'
                          label='Ativo'
                          fdisabled={isLoading}
                          aliamento='start'
                        />
                      </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                      <VTextField
                        fullWidth
                        name='obs'
                        disabled={isLoading}
                        label='Observações'
                        multiline
                        rows={4}
                      />
                    </Grid>
                    <Grid alignContent={'center'} item xs={12} sm={12} md={9} lg={8} xl={12}>
                      <MenuBookIcon onClick={() => { setOpenFiles(true); }} color='primary' sx={{ fontSize: 28 }} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <DialogImagens
            id={id}
            value={''}
            open={open}
            onClose={handleClose}
            dados={images}
            onDados={handleImg}
          />
          <DialogFiles
            id={id}
            value={''}
            open={openfiles}
            onClose={handleCloseFiles}
            dados={arquivos}
            onDados={handleFiles}
          />
        </Box>
      </VForm>
    </LayoutBaseDePagina>
  );
};